import { type PayloadAction, createSlice } from '@reduxjs/toolkit';

/**
 * Global loading state for full-screen overlay
 *
 * Uses a count-based system to handle multiple simultaneous operations:
 * - Each operation increments the count when starting
 * - Each operation decrements the count when finishing
 * - Overlay is visible when count > 0
 *
 * This prevents the overlay from disappearing prematurely when
 * multiple operations are in progress.
 */
interface LoadingState {
  isLoading: boolean;
  message: string;
  count: number;
}

const initialState: LoadingState = {
  isLoading: false,
  message: 'Loading...',
  count: 0,
};

const loadingSlice = createSlice({
  name: 'loading',
  initialState,
  reducers: {
    /**
     * Start loading - increments count and shows overlay
     * @param action.payload - Optional custom message to display
     */
    startLoading: (state, action: PayloadAction<string | undefined>) => {
      state.count += 1;
      state.isLoading = true;
      state.message = action.payload || 'Loading...';
    },

    /**
     * Stop loading - decrements count and hides overlay if count reaches 0
     */
    stopLoading: (state) => {
      state.count = Math.max(0, state.count - 1);
      state.isLoading = state.count > 0;
    },

    /**
     * Reset loading - immediately hides overlay regardless of count
     * Use with caution - only for error recovery scenarios
     */
    resetLoading: (state) => {
      state.count = 0;
      state.isLoading = false;
      state.message = 'Loading...';
    },
  },
});

export const { startLoading, stopLoading, resetLoading } = loadingSlice.actions;
export default loadingSlice.reducer;
